//@flow
import {useSelector} from 'react-redux';
import {useEffect, useState} from 'react';

import {
  createStoreInformationBlockService,
  deleteStoreInformationBlockService,
  getStoreInformationBlockService,
  updateStoreInformationBlockService,
} from '../promises/information-block';

import {useActions} from '../../../utils/globalStore';
import {useToastActions} from '../../toast-alert/utils';

import {actions} from '../duck';

import type {
  CreateStoreInformationBlockRequest,
  UpdateStoreInformationBlockRequest,
  StoreInformationBlockContentRequest,
} from '../promises/information-block';
import type {StoreInformationBlock} from '../../../entities/store-information-block';

import type {RootState} from '../..';
import {uploadImageService} from '../../file-storage/promises';
import {compressImageIfNecessary} from '../../../utils/image';

const informationBlocksSelector = (state: RootState) =>
  state.homePage.storeInformationBlocks;

const informationBlocksLoadingSelector = (state: RootState) =>
  state.homePage.isFetchingStoreInformationBlocks;

export function useStoreInformationBlocks() {
  const informationBlocks = useSelector(informationBlocksSelector);
  const loading = useSelector(informationBlocksLoadingSelector);

  const fetchStoreInformationBlocks = useActions(
    actions.getStoreInformationBlocks
  );

  useEffect(() => {
    if (informationBlocks) return;
    fetchStoreInformationBlocks();
  }, [fetchStoreInformationBlocks, informationBlocks]);

  const refreshBlocks = () => {
    fetchStoreInformationBlocks();
  };
  return {
    refreshBlocks,
    informationBlocks: informationBlocks ?? [],
    loading,
  };
}

type CreateStoreInformationBlockParams = {
  storeInformationBlockType: 'COLLAPSIBLE' | 'TEXT_IMAGE' | 'TEXT_LIST',
  title: string,
  description?: string,
  subtitle?: string,
  enabled: boolean,
  content: {
    description: string,
    title: string,
    file: File,
  }[],
};

type UpdateStoreInformationBlockParams = {
  storeInformationBlockType?: 'COLLAPSIBLE' | 'TEXT_IMAGE' | 'TEXT_LIST',
  title?: string,
  description?: string,
  subtitle?: string,
  enabled?: boolean,
  content?: {
    description: string,
    title: string,
    imageId?: string,
    file?: File,
  }[],
};

export function useStoreInformationBlockActions() {
  const [loading, setLoading] = useState<boolean>(false);

  const [error, setError] = useState<string | null>(null);

  const {showToast} = useToastActions();

  const createStoreInformationBlock = async (
    block: CreateStoreInformationBlockParams
  ) => {
    setLoading(true);
    try {
      const contentList: StoreInformationBlockContentRequest[] = [];

      for (const content of block.content) {
        const sizeThreshold = 1024 * 1024; // 1MB
        const file = await compressImageIfNecessary(content.file, {
          maxWidth: 1024,
          maxHeight: 1024,
          threshold: sizeThreshold,
          quality: 0.85,
          customFormat: 'jpeg',
        });

        const [image] = await uploadImageService(
          file,
          'ui/store-information-block'
        );

        const contentRequest: StoreInformationBlockContentRequest = {
          description: content.description,
          title: content.title,
          imageId: image.id,
        };
        contentList.push(contentRequest);
      }

      const request: CreateStoreInformationBlockRequest = {
        enabled: block.enabled,
        storeInformationBlockType: block.storeInformationBlockType,
        title: block.title,
        description: block.description,
        subtitle: block.subtitle,
        content: contentList,
      };

      const createdBlock = await createStoreInformationBlockService(request);
      return createdBlock;
    } catch (err) {
      showToast({
        type: 'danger',
        title: 'Error',
        message: 'Ocurrió un error al crear el bloque de información.',
      });
      console.log({err});
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateStoreInformationBlock = async (
    blockId: string,
    block: UpdateStoreInformationBlockParams
  ) => {
    setLoading(true);
    try {
      const contentList: StoreInformationBlockContentRequest[] = [];

      if (block.content) {
        for (const content of block.content) {
          const sizeThreshold = 1024 * 1024; // 1MB

          let fileId = content.imageId;

          if (content.file) {
            const file = await compressImageIfNecessary(content.file, {
              maxWidth: 1024,
              maxHeight: 1024,
              threshold: sizeThreshold,
              quality: 0.85,
              customFormat: 'jpeg',
            });

            const [image] = await uploadImageService(
              file,
              'ui/store-information-block'
            );

            fileId = image.id;
          }

          if (fileId) {
            const contentRequest: StoreInformationBlockContentRequest = {
              description: content.description,
              title: content.title,
              imageId: fileId,
            };
            contentList.push(contentRequest);
          }
        }
      }

      const request: UpdateStoreInformationBlockRequest = {
        enabled: block.enabled,
        storeInformationBlockType: block.storeInformationBlockType || undefined,
        title: block.title || undefined,
        description: block.description || undefined,
        subtitle: block.subtitle || undefined,
        content: contentList.length > 0 ? contentList : undefined,
      };

      const updatedBlock = await updateStoreInformationBlockService(
        blockId,
        request
      );
      return updatedBlock;
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteStoreInformationBlock = async (blockId: string) => {
    setLoading(true);
    try {
      await deleteStoreInformationBlockService(blockId);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    createStoreInformationBlock,
    updateStoreInformationBlock,
    deleteStoreInformationBlock,
    loading,
    error,
  };
}

export function useStoreInformationBlock(id?: string | null) {
  const [block, setBlock] = useState<StoreInformationBlock | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getStoreInformationBlockService(id)
      .then(block => {
        setBlock(block);
      })
      .catch(error => {
        console.error('Error fetching block:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  return {
    block,
    loading,
  };
}
